import React, { useState, ChangeEvent, useContext } from 'react'
import { WithGlobalStylesProps } from 'types/styles/WithGlobalStylesProps'
import InviteUserModalStyles from './InviteUserModal.styles'
import { compose } from 'recompose'
import { withGlobalStyles } from 'Support/Theme/CreateInjectableStyles/withGlobalStyles'
import ModalComponent from 'Components/Modal/Modal.component'
import Grid from '@material-ui/core/Grid'
import PersonAddIcon from '@material-ui/icons/PersonAdd'
import EmailIcon from '@material-ui/icons/Email'
import { Button, TextField, Typography, FormControl, RadioGroup, FormControlLabel, Radio, Paper, InputAdornment } from '@material-ui/core'
import Validation from 'Support/Validation'
import cn from 'classnames'
import { useDispatch } from 'react-redux'
import uiTriggered from 'State/Store/UI/Actions/UI.Triggered.action'
import { UI_USER_INVITED, UI_ACCOUNT_PAGE_INVITE_DIALOGUE_STANDARD_USER_CLICK, UI_ACCOUNT_PAGE_INVITE_DIALOGUE_SUPER_USER_CLICK, UI_ACCOUNT_PAGE_INVITE_DIALOGUE_SEND_INVITATION_BTN, UI_ACCOUNT_PAGE_INVITE_DIALOGUE_CANCEL } from 'State/Store/UI/UI.types'
import TextSupport from 'Support/Text'
import ServicesContext from 'Contexts/Services/Services.context'
import { openSnackbar, SNACKBAR_GENERAL_ERROR_MESSAGE } from 'State/Store/Snackbar/SnackbarActionCreators'
import hitGTM from 'State/Store/UI/Actions/GTM.action'

interface Props {
  open: boolean,
  onClose(): void,
  accountId: any,
  account: any,
}

type FProps = Props & WithGlobalStylesProps<any>

type RoleType = 'Standard' | 'Super'
const InviteUserModalComp = (props: FProps) => {
  const {
    classes,
    open,
    onClose,
    accountId,
    account,
  } = props
  const [ loading, setLoading ] = useState(false)
  const [ email, setEmail ] = useState('')
  const [ selectedRole, setSelectedRole ] = useState<RoleType>('Standard')
  const dispatch = useDispatch()
  const services = useContext(ServicesContext)
  const handleClose = () => {
    dispatch(hitGTM(UI_ACCOUNT_PAGE_INVITE_DIALOGUE_CANCEL))
    onClose()
  }

  const inviteUser = async () => {
    try {
      dispatch(hitGTM(UI_ACCOUNT_PAGE_INVITE_DIALOGUE_SEND_INVITATION_BTN))
      setLoading(true)
      dispatch(uiTriggered({
        payload: {
          data: {
            name: `${selectedRole}UserInvited`,
          },
        },
        type: UI_USER_INVITED,
      }))
      const invitedUser = await services.userService.inviteUser(email, selectedRole, TextSupport.getNumberFromBillTo(accountId), account.AccountName)
      onClose()
      setLoading(false)
    } catch (ex) {
      const errorMessage = TextSupport.getErrorMessage(ex.message)
      dispatch(openSnackbar({ message: errorMessage }))
      setLoading(false)
    }
  }
  const content = (
    <div className={classes.contentWrapper}>
      <Typography variant='body1'>
        { `Send an invitation to:` }
      </Typography>

      <TextField
        autoFocus
        className={classes.marginBottom3unit}
        fullWidth
        id='name'
        onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
        type='email'
        value={email || ``}
        placeholder={`recipient@example.com`}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <EmailIcon className={classes.emailIcon} />
            </InputAdornment>
          ),
        }}
      />
      <Typography variant='body1'>
        { `Set role:` }
      </Typography>
      <FormControl>
        <div
          className={classes.radioWrapper}
          onClick={() => {
            dispatch(hitGTM(UI_ACCOUNT_PAGE_INVITE_DIALOGUE_SUPER_USER_CLICK))
            setSelectedRole('Super')
          }
        }>
          <Paper className={classes.radioPaper}>
            <Radio
              name='role'
              color='primary'
              value='Super'
              checked={selectedRole === 'Super'}
            />
            <div className={classes.radioRight}>
              <Typography variant='h6'>
                { `Super User` }
              </Typography>
              <Typography variant='body2'>
                Super Users have the permission on this account to manage (add and/or remove) users and manage orders and deliveries
              </Typography>
            </div>
          </Paper>
        </div>
        <div
          className={classes.radioWrapper}
          onClick={() => {
            dispatch(hitGTM(UI_ACCOUNT_PAGE_INVITE_DIALOGUE_STANDARD_USER_CLICK))
            setSelectedRole('Standard')
          }
        }>
          <Paper className={classes.radioPaper}>
            <Radio
              name='role'
              color='primary'
              value='Standard'
              checked={selectedRole === 'Standard'}
            />
            <div className={classes.radioRight}>
              <Typography variant='h6'>
                { `Standard User` }
              </Typography>
              <Typography variant='body2'>
                Standard Users have the permission on this account to manage orders and deliveries.
              </Typography>
            </div>
          </Paper>
        </div>
      </FormControl>
      <Typography variant='body2' className={cn(classes.alertText, classes.marginTop2unit, classes.marginBottom2unit)}>
        { `You can change these settings later.` }
      </Typography>
      <Typography variant='body2' className={cn(classes.alertText)}>
        { `A link will be sent to the above email addresses. The user will be activated after they click the link, set a password and complete their user profile. The link will expire 30 days after it is sent.` }
      </Typography>
    </div>
  )
  return (
    <ModalComponent
      maxWidth='sm'
      fullWidth
      open={open}
      onClose={handleClose}
      rightHeading={null}
      title={'Invite People'}
      icon={<PersonAddIcon />}
      content={content}
      footer={(
        <Grid className={classes.textAlignRight} container justify='flex-end' spacing={2}>
          <Grid className={classes.flexGrow1} item>
            <Button
              className={classes.button}
              variant='contained'
              disabled={loading}
              onClick={handleClose}
            >
              { `Cancel` }
            </Button>
          </Grid>
          <Grid item>
            <Button
              className={classes.button}
              color='primary'
              variant='contained'
              disabled={!Validation.validateEmail(email) || loading}
              onClick={() => {
                if (Validation.validateEmail(email)) {
                  inviteUser()
                }
              }}
            >
              { `Send Invitation` }
            </Button>
          </Grid>
        </Grid>
      )}
    />
  )
}

export default compose<FProps, Props>(
  withGlobalStyles(InviteUserModalStyles),
)(InviteUserModalComp)
